/**
 * UptimeWidget — public 90-day green-state strip
 * ─────────────────────────────────────────────
 * Renders a horizontal strip of 90 cells (one per UTC day, oldest →
 * newest). Cells are colour-coded by /api/trust/uptime status:
 *   up       → emerald
 *   partial  → amber
 *   down     → rose
 *   unknown  → slate (before earliest audit entry — we don't guess)
 *
 * Summary row below shows: current streak (days), percent uptime, and
 * absolute counts. Cached client-side for the 5-min TTL of the API
 * response — no polling.
 */
import React, { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  up:      { fill: 'bg-emerald-500', label: 'up'      },
  partial: { fill: 'bg-amber-500',   label: 'partial' },
  down:    { fill: 'bg-rose-500',    label: 'down'    },
  unknown: { fill: 'bg-slate-300',   label: 'unknown' },
};

export default function UptimeWidget({ isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/trust/uptime`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setData(j); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div
        className={`rounded-lg border p-6 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-white'}`}
        data-testid="trust-uptime-widget-loading"
      >
        <div className="text-xs opacity-60">Loading uptime…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={`rounded-lg border p-6 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-white'}`}
        data-testid="trust-uptime-widget-error"
      >
        <div className="text-xs text-rose-500">Could not load uptime data.</div>
      </div>
    );
  }

  const { days = [], summary = {}, window_days: windowDays = 90 } = data;

  return (
    <div
      className={`rounded-lg border p-6 md:p-8 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-white'}`}
      data-testid="trust-uptime-widget"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[9px] uppercase tracking-[0.28em] font-bold mb-1" style={{ color: 'var(--ve-eyebrow)' }}>
            [UPTIME · {windowDays} DAYS]
          </div>
          <h3 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Activity size={18} style={{ color: 'var(--ve-violet-bright)' }} />
            Public operations record
          </h3>
          <p className={`mt-1 text-xs leading-relaxed max-w-xl ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            Derived from the hash-chained audit log — a day counts as
            "up" when the platform recorded ≥12 signed entries that UTC
            day. No self-reported dashboards.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            data-testid="trust-uptime-percent"
            className="pal-mono text-2xl font-bold tabular-nums"
            style={{ color: 'var(--ve-violet-bright)' }}
          >
            {(summary.percent_up || 0).toFixed(2)}%
          </span>
          <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
            uptime (excl. unknown)
          </span>
        </div>
      </div>

      {/* 90-day strip */}
      <div
        className="flex items-end gap-[2px] w-full"
        role="img"
        aria-label={`${windowDays}-day uptime strip. ${summary.up || 0} up, ${summary.partial || 0} partial, ${summary.down || 0} down.`}
        data-testid="trust-uptime-strip"
      >
        {days.map((d) => {
          const style = STATUS_STYLE[d.status] || STATUS_STYLE.unknown;
          return (
            <div
              key={d.date}
              data-testid={`trust-uptime-cell-${d.date}`}
              data-status={d.status}
              title={`${d.date} — ${style.label} (${d.count} entries)`}
              className={`flex-1 h-8 rounded-sm ${style.fill} ${d.status === 'unknown' ? 'opacity-30' : 'opacity-90 hover:opacity-100'} transition-opacity`}
            />
          );
        })}
      </div>

      {/* Legend + summary */}
      <div className={`mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
        <LegendDot color="bg-emerald-500" label={`${summary.up || 0} up`} testId="trust-uptime-legend-up" />
        <LegendDot color="bg-amber-500"   label={`${summary.partial || 0} partial`} testId="trust-uptime-legend-partial" />
        <LegendDot color="bg-rose-500"    label={`${summary.down || 0} down`} testId="trust-uptime-legend-down" />
        <LegendDot color="bg-slate-300 opacity-30" label={`${summary.unknown || 0} unknown`} testId="trust-uptime-legend-unknown" />
        <div className="ml-auto pal-mono uppercase tracking-[0.22em] text-[10px] font-bold" data-testid="trust-uptime-streak">
          Current streak · {summary.current_streak_days || 0}d
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className={`inline-block h-3 w-3 rounded-sm ${color}`} />
      <span>{label}</span>
    </div>
  );
}
